import { Api, LicenseTester, Model, StringUtil } from "@mcleod/core";
import {
    DataSourceExecutionEvent, LayoutProvidedCrudDecoratorProps, DataSourceModeChangeEvent, DataSourceMode,
    Textbox,
    Tab,
    DataDisplayEvent
} from "@mcleod/components";
import { CopyUser, UserLayoutProvider, UserUtil, WebPasswordChange } from "@mcleod/common";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { AutogenLayoutLMEUser } from "./autogen/AutogenLayoutLMEUser";
import { UsersResponsibilitySlideoutMF } from "../UsersResponsibilitySlideoutMF";
import { ResponsibilityUtil } from "../ResponsibilityUtil";

const viewUserURL = `${UserLayoutProvider.get().layoutPath}?mode=update&key=`;

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "users",
    screenName: "User",
    navigateToUrl: viewUserURL
};

export class LMEUser extends AutogenLayoutLMEUser {
    private allowManualEntry: boolean;
    private codeGenerationIsActive: boolean;

    override onLoad() {
        UserUtil.setAdminComponentVisibility(this.panelAdminButtons);
        UserUtil.handleScrollToTab(this, this.tabsetUsers);
        const responsibilityTab: Tab = this.tabsetUsers.findComponentById("tabResponsibility") as Tab;
        if (responsibilityTab) {
            UserUtil.setAdminComponentVisibility(responsibilityTab);
        }
    }

    async displayUserAgenciesSlideout() {
        let enableCommissions = false;
        let enableCustomerCommissions = false;
        const payrollControlResponse = await Model.searchSingleRecord("lme/dispatch/payroll-control")
        enableCommissions = payrollControlResponse?.getBoolean("enable_commissions");

        const agentAccessResponse = await Model.searchSingleRecord("lme/dispatch/agent-access-ctrl")
        enableCustomerCommissions = agentAccessResponse?.getBoolean("auto_assign_cus_commissions");
        const urs = UsersResponsibilitySlideoutMF.builder()
            .layoutName("lme/general/user/LMEUserAgencyUsers")
            .title(this.getUserAgenciesSlideoutTitle())
            .agenciesLayoutHeaderTitle("Responsibility Codes")
            .customeSlideoutWidth(enableCommissions ? UserUtil.SLIDEOUT_MAX_WIDTH : "50%")
            .idFilter(this.getUserAgenciesIdFilter())
            .parentMainDataSource(this.sourceUserSettings)
            .parentAgencyLinkDataSource(this.sourceAgencyLink)
            .primaryAgencyField(this.getUserPrimaryAgencyField())
            .enableCommissions(enableCommissions)
            .enableCustomerCommissions(enableCustomerCommissions)
            .build();
        urs.show();
    }

    private getUserAgenciesSlideoutTitle(): string {
        const name = this.sourceUserSettings.activeRow?.get("id");
        return "User Settings" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getUserAgenciesIdFilter(): any {
        const id = this.sourceUserSettings.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getUserPrimaryAgencyField(): string {
        return ResponsibilityUtil.getPrimaryAgencyField();
    }

    sourceUserSettingsAfterExecution(event: DataSourceExecutionEvent) {
        const userBeingUpdated = this.sourceUserSettings.activeRow.get("id");
        UserUtil.triggerSettingsRefresh(userBeingUpdated, this, event);
    }

    override get crudDecoratorProps(): LayoutProvidedCrudDecoratorProps {
        return UserUtil.getCrudDecoratorProps();
    }

    async buttonUserGroupsOnClick() {
        const userId = this.activeRow?.get("id", "") ?? "";
        const userName = this.activeRow?.get("name", "") ?? "";
        UserUtil.displayGroupMembership(userId, userName);
    }

    buttonSetWebPasswordOnClick() {
        const endpointPath = "lme/general/user/change-web-password";
        const userId = this.sourceUserSettings.activeRow.get("id", null);
        const userName = this.sourceUserSettings.activeRow.get("name", null);
        new WebPasswordChange(endpointPath, userId, userName).show();
    }

    buttonCopyUserOnClick() {
        const endpointPath = "lme/general/user/copy";
        const sourceUserId = this.sourceUserSettings.activeRow.get("id", null);
        const sourceUserName = this.sourceUserSettings.activeRow.get("name", null);
        new CopyUser(endpointPath, sourceUserId, sourceUserName).fillAndShow();
    }

    sourceUserSettingsAfterModeChange(event: DataSourceModeChangeEvent) {
        const toolbarButtonsVisible = event.newMode === DataSourceMode.UPDATE;
        this.buttonUserGroups.visible = toolbarButtonsVisible;
        this.buttonSetWebPassword.visible = toolbarButtonsVisible;
        this.buttonCopyUser.visible = toolbarButtonsVisible;
    }

    sourceUserSettingsBeforeModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            const textboxId = (this.layoutUserGeneral as any).textboxId as Textbox;
            textboxId.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "users" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                if (this.codeGenerationIsActive) {
                    textboxId.enabledDuringAdd = this.allowManualEntry;
                    textboxId.validationWarning = null;
                    textboxId.required = false;
                }
                DuplicateId.configureIdTextbox(textboxId, {
                    ...DUPLICATE_VALIDATION_PROPS,
                    codeGenerationIsActive: this.codeGenerationIsActive
                });
            });
        }
    }

    /** This is an event handler for the onDisplay event of sourceUserSettings. */
    sourceUserSettingsOnDisplay(event: DataDisplayEvent) {
        if (this.sourceUserSettings.activeRow) {
            if (this.sourceUserSettings.activeRow.get("allowSetWebPassword") === false) {
                this.buttonSetWebPassword.enabled = false;
                this.buttonSetWebPassword.disabledTooltip = "Cannot set a web password for hosted users.";
                this.buttonSetWebPassword.tooltip = null;
                this._syncEnabled();
            } else {
                this.buttonSetWebPassword.enabled = true;
                this.buttonSetWebPassword.disabledTooltip = null;
                this.buttonSetWebPassword.tooltip = "Change the user\u0027s web password";
                this._syncEnabled();
            }
        }
    }
}
